import { useEffect } from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import {
  currencyFormat,
  minifyString as minify,
  minifyString,
  numberFormat
} from '@dor/utils';
import { Stats } from '@/common/components/Stats/component.tsx';
import { Table } from '@/common/components/Table/component.tsx';
import { useSnapshotsProvider } from '@/common/providers/SnapshotsProvider/provider.tsx';
import { useNetworkStatsProvider } from '@/common/providers/NetworkStatsProvider/provider.tsx';
import { useRewardsProvider } from '@/common/providers/RewardsProvider/provider.tsx';
import { useTransactionsProvider } from '@/common/providers/TransactionsProvider/provider.tsx';
import { ViewLayout } from '@/common/components/ViewLayout/component.tsx';
import { useDevicesProvider } from '@/common/providers/DevicesProvider/provider.tsx';
import {
  DeploymentDAGExplorerBaseUrl,
  DeploymentMetagraphId
} from '@/common/consts/api.ts';
import { CopyableLinkableContent } from '@/common/components/CopyableLinkableContent/component.tsx';

import styles from './view.module.scss';

const FeesPaidTooltipContent = (
  <p className={styles.totalCirculationTooltip}>
    Total tokens removed from circulation by Constellation for metagraph
    development, support, and continuous data integration supplied by DTMs. This
    includes, but is not limit to, license fees, tokens for coupon sales, and
    API access.
    <br />
    <br />
    <b>CCLF wallet address</b>
    <br />
    <CopyableLinkableContent
      className={styles.link}
      value="DAG2uFJWUMHW8pZXXeaUMtUpWwZvhpr3BBBtBYEp"
      linkTo={
        'https://mainnet.dagexplorer.io/address/DAG2uFJWUMHW8pZXXeaUMtUpWwZvhpr3BBBtBYEp'
      }
      isExternal
      content="DAG2uFJWUMHW8pZXXeaUMtUpWwZvhpr3BBBtBYEp"
    />
  </p>
);

const TotalCirculationTooltipContent = (
  <p className={styles.totalCirculationTooltip}>
    The total circulation figure does not include DOR:
    <br />
    • reserved for the 2-year Lattice Commission Pool
    <br />• pleged as node collateral <br />
    • in the Constellation’s Commercial License Fee wallet
    <br />
    <br />
    <b>CCLF wallet address</b>
    <br />
    <CopyableLinkableContent
      className={styles.link}
      value="DAG2uFJWUMHW8pZXXeaUMtUpWwZvhpr3BBBtBYEp"
      linkTo={
        'https://mainnet.dagexplorer.io/address/DAG2uFJWUMHW8pZXXeaUMtUpWwZvhpr3BBBtBYEp'
      }
      isExternal
      content="DAG2uFJWUMHW8pZXXeaUMtUpWwZvhpr3BBBtBYEp"
    />
  </p>
);

export const DashboardView = () => {
  const networkStats = useNetworkStatsProvider();
  const snapshots = useSnapshotsProvider();
  const devices = useDevicesProvider();
  const rewards = useRewardsProvider();
  const transactions = useTransactionsProvider();

  const fetchInformation = () => {
    networkStats.stats.fetch({}, {});
    snapshots.snapshots.fetch({}, {}, { pageSize: 5 });
    devices.checkins.fetch({}, {}, { pageSize: 5 });
    rewards.rewards.fetch({}, {}, { pageSize: 5 });
    transactions.transactions.fetch({}, {}, { pageSize: 5 });
  };

  useEffect(() => {
    fetchInformation();
    const iid = setInterval(fetchInformation, 10 * 1000);

    return () => {
      clearInterval(iid);
    };
  }, []);

  return (
    <ViewLayout>
      <Stats>
        <Stats.StatsGroup title="Overview">
          <Stats.Stat
            flex="30%"
            title="Active Sensor Nodes"
            mainValue={
              numberFormat(
                networkStats.stats.resource.resource?.activeSensorCount,
                'MILLIFY'
              ) ?? '...'
            }
            secondaryValue={`${
              numberFormat(
                networkStats.stats.resource.resource?.activeSensorCount,
                'DECIMAL'
              ) ?? '...'
            } Nodes`}
          />
          <Stats.Stat
            flex="30%"
            title="Total Snapshots"
            mainValue={
              numberFormat(
                networkStats.stats.resource.resource?.totalSnapshots,
                'MILLIFY'
              ) ?? '...'
            }
            secondaryValue={`${numberFormat(
              networkStats.stats.resource.resource?.totalSnapshots,
              'MILLIFY'
            )} Snapshots`}
          />
          <Stats.Stat
            flex="30%"
            title="DOR Out of Circulation"
            tooltipContent={FeesPaidTooltipContent}
            mainValue={
              numberFormat(
                networkStats.stats.resource.resource?.feesPaid,
                'MILLIFY',
                8
              ) ?? '...'
            }
            secondaryValue={currencyFormat(
              'DOR',
              networkStats.stats.resource.resource?.feesPaid,
              'DECIMAL',
              8
            )}
          />
          <Stats.Stat
            flex="17%"
            title="Validator Nodes - L0"
            mainValue={
              networkStats.stats.resource.resource?.validatorNodesL0 ?? '...'
            }
            secondaryValue="Currently Active"
          />
          <Stats.Stat
            flex="17%"
            title="Validator Nodes - L1"
            mainValue={
              networkStats.stats.resource.resource?.validatorNodesL1 ?? '...'
            }
            secondaryValue="Currently Active"
          />
          <Stats.Stat
            flex="17%"
            title="Total DAG Locked"
            mainValue={numberFormat(
              networkStats.stats.resource.resource?.totalDagLocked,
              'MILLIFY',
              8
            )}
            secondaryValue={currencyFormat(
              'DAG',
              networkStats.stats.resource.resource?.totalDagLocked,
              'DECIMAL',
              8
            )}
          />
          <Stats.Stat
            flex="17%"
            title="Total DOR Locked"
            mainValue={numberFormat(
              networkStats.stats.resource.resource?.totalDorLocked,
              'MILLIFY',
              8
            )}
            secondaryValue={currencyFormat(
              'DOR',
              networkStats.stats.resource.resource?.totalDorLocked,
              'DECIMAL',
              8
            )}
          />
          <Stats.Stat
            flex="17%"
            title="Total DOR In Circulation"
            tooltipContent={TotalCirculationTooltipContent}
            mainValue={numberFormat(
              networkStats.stats.resource.resource?.totalDorInCirculation,
              'MILLIFY',
              8
            )}
            secondaryValue={currencyFormat(
              'DOR',
              networkStats.stats.resource.resource?.totalDorInCirculation,
              'DECIMAL',
              8
            )}
          />
        </Stats.StatsGroup>
      </Stats>
      <div className={styles.information}>
        <Table
          title="Device Check-ins"
          columns={[
            { key: 'deviceId', title: 'device id', style: 'link' },
            { key: 'timestamp', title: 'timestamp' },
            { key: 'dataHash', title: 'Data Hash' },
            {
              key: 'ordinal',
              title: 'ordinal',
              style: 'link'
            }
          ]}
          data={devices.checkins.resource.resource.map((checkin) => ({
            timestamp: (
              <span title={dayjs.unix(checkin.dts).toISOString()}>
                {dayjs.unix(checkin.dts).fromNow()}
              </span>
            ),
            deviceId: (
              <Link to={`/devices/${checkin.proof.id}`}>
                {minify(checkin.proof.id)}
              </Link>
            ),
            dataHash: minify(checkin.checkInHash),
            ordinal: (
              <Link to={`/snapshots/${checkin.ordinal}`}>
                {checkin.ordinal}
              </Link>
            )
          }))}
          viewMoreLinkTo="/devices/checkins"
          pleaseCheckBackMessage
        />
        <Table
          title="DOR Commissions"
          columns={[
            { key: 'toAddress', title: 'to', style: 'link' },
            { key: 'ordinal', title: 'Ordinal' },
            { key: 'amount', title: 'amount' }
          ]}
          data={rewards.rewards.resource.resource.map((reward) => ({
            toAddress: (
              <a
                href={`${DeploymentDAGExplorerBaseUrl}/address/${reward.address}`}
                target="_blank"
              >
                {minifyString(reward.address, 4, 4)}
              </a>
            ),
            ordinal: (
              <Link to={`/snapshots/${reward.snapshotOrdinal}`}>
                {reward.snapshotOrdinal}
              </Link>
            ),
            amount: currencyFormat('DOR', reward.amount, 'DECIMAL', 8)
          }))}
          viewMoreLinkTo="/commissions"
          pleaseCheckBackMessage
        />
        <Table
          className={styles.fullWidth}
          title="Snapshots"
          columns={[
            {
              key: 'ordinal',
              title: 'ordinal',
              style: 'link'
            },
            {
              key: 'hash',
              title: 'Hash'
            },
            {
              key: 'checkins',
              title: 'Device Check-ins'
            },
            { key: 'blocks', title: 'Currency Blocks' }
          ]}
          data={snapshots.snapshots.resource.resource.map((snapshot) => ({
            ordinal: (
              <Link to={`/snapshots/${snapshot.value.ordinal}`}>
                {snapshot.value.ordinal}
              </Link>
            ),
            hash: minify(snapshot.value.lastSnapshotHash),
            checkins: snapshot.deviceCheckins,
            blocks: snapshot.value.blocks.length
          }))}
          pleaseCheckBackMessage
          viewMoreLinkTo="/snapshots"
        />
        <Table
          className={styles.fullWidth}
          title="DOR Transactions"
          columns={[
            { key: 'trxHash', title: 'Txn Hash', style: 'link' },
            { key: 'timestamp', title: 'timestamp' },
            { key: 'snapshotHash', title: 'snapshot', style: 'link' },
            { key: 'trxFee', title: 'Fee' },
            { key: 'fromAddress', title: 'From', style: 'link' },
            { key: 'toAddres', title: 'To', style: 'link' },
            { key: 'amount', title: 'Amount' }
          ]}
          data={transactions.transactions.resource.resource.map(
            (transaction) => ({
              trxHash: (
                <a
                  href={`${DeploymentDAGExplorerBaseUrl}/metagraphs/${DeploymentMetagraphId}/transactions/${transaction.hash}`}
                  target="_blank"
                >
                  {minify(transaction.hash, 12, 12)}
                </a>
              ),
              timestamp: dayjs.utc(transaction.updatedAt).fromNow(),
              snapshotHash: (
                <Link to={`/snapshots/${transaction.snapshotOrdinal}`}>
                  {transaction.snapshotOrdinal}
                </Link>
              ),
              trxFee: currencyFormat('DOR', transaction.fee, 'DECIMAL', 8),
              fromAddress: (
                <a
                  href={`${DeploymentDAGExplorerBaseUrl}/address/${transaction.source}`}
                  target="_blank"
                >
                  {minifyString(transaction.source, 5, 5)}
                </a>
              ),
              toAddres: (
                <a
                  href={`${DeploymentDAGExplorerBaseUrl}/address/${transaction.destination}`}
                  target="_blank"
                >
                  {minifyString(transaction.destination, 5, 5)}
                </a>
              ),
              amount: currencyFormat('DOR', transaction.amount, 'DECIMAL', 8)
            })
          )}
          pleaseCheckBackMessage
        />
      </div>
    </ViewLayout>
  );
};
